/**
 * SyncBadge.js
 *
 * Floating badge showing the count of Pending + Failed orders.
 * Tapping opens the PendingOrdersModal.
 */

import React, {memo, useCallback, useState} from 'react';
import {StyleSheet, Text, TouchableOpacity} from 'react-native';
import Icon from 'react-native-vector-icons/Feather';
import {useOfflineSyncContext} from '../helpers/OfflineSyncContext';
import PendingOrdersModal from './PendingOrdersModal';

const SyncBadge = () => {
  const {
    totalUnsyncedCount,
    pendingCount,
    failedCount,
    syncingCount,
    pendingOrders,
    loadPendingOrders,
    retryAll,
    retryOne,
    removeOrder,
    isOnline,
    refreshCounts,
  } = useOfflineSyncContext();

  const [modalVisible, setModalVisible] = useState(false);

  const handlePress = useCallback(async () => {
    await Promise.all([loadPendingOrders(), refreshCounts()]);
    setModalVisible(true);
  }, [loadPendingOrders, refreshCounts]);

  const handleClose = useCallback(() => {
    setModalVisible(false);
  }, []);

  // Don't render anything if there's nothing to show
  if (totalUnsyncedCount === 0) return null;

  // Choose badge colour based on severity
  const hasFailed = failedCount > 0;
  const badgeColor = hasFailed ? '#dc3545' : '#f0ad4e';

  return (
    <>
      <TouchableOpacity
        style={[styles.badge, { backgroundColor: badgeColor }]}
        onPress={handlePress}
        activeOpacity={0.7}
        accessibilityRole="button"
        accessibilityLabel={`${totalUnsyncedCount} orders pending sync`}
      >
        <Icon
          name={syncingCount > 0 ? 'refresh-cw' : 'cloud-off'}
          size={16}
          color="#fff"
        />
        <Text style={styles.count}>{totalUnsyncedCount}</Text>
      </TouchableOpacity>

      <PendingOrdersModal
        visible={modalVisible}
        onClose={handleClose}
        orders={pendingOrders}
        onRetryAll={retryAll}
        onRetryOne={retryOne}
        onRemoveOrder={removeOrder}
        isOnline={isOnline}
        pendingCount={pendingCount}
        failedCount={failedCount}
        syncingCount={syncingCount}
      />
    </>
  );
};

const styles = StyleSheet.create({
  badge: {
    position: 'absolute',
    top: 40,
    right: 16,
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 20,
    elevation: 6,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    zIndex: 9998,
  },
  count: {
    color: '#fff',
    fontWeight: '700',
    fontSize: 14,
    marginLeft: 6,
  },
});

export default memo(SyncBadge);


